import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { SnippetBox } from '@gamma/ui/display';
import { AccordionPanel, Board, Column } from '@gamma/ui/layout';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import moment from 'moment';
import { TimestampCell } from '@investigator/shared/components';
import { i18n } from '@investigator/shared/localization';
import {
  QUERY_ALERTS_SURICATA_PAYLOAD,
  QUERY_ALERTS_SURICATA_PAYLOAD_LLM_DISABLED,
  SessionSummaryData,
} from '../../../../../../../../../../queries';
import { buildAlertIdQuery } from '../../utils/buildAlertIdQuery';

import { useLazyQuery } from '@apollo/client';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useHumioToken } from '@investigator/shared/providers';
import { Modal } from '@gamma/ui/overlay';
import {
  DetailsGrid,
  DetailsGridRow,
} from '@investigator/features/detections/components';
import dedent from 'ts-dedent';
import {
  AlertSessionSummary,
  Maybe,
  QueryAlertsSuricataPayloadLLmDisabledPaginatedQuery,
  QueryAlertsSuricataPayloadLLmDisabledPaginatedQueryVariables,
  QueryAlertsSuricataPayloadPaginatedQuery,
  QueryAlertsSuricataPayloadPaginatedQueryVariables,
} from '@investigator/shared/generated/graphql';
import { ChatGPTIcon } from '../../../../../../../../../../../entities/components';
import { SanitizedSuricataAlert } from '../../../DetectionAlerts';
import { InvestigateLogsTableModal } from '../InvestigateLogsTableModal';
import { LogscaleDetails } from '../LogscaleDetails';
import { buildSearchQueryString } from '../LogscaleDetails/utils/buildSearchQueryString';
import { LogscaleMenuButton } from '../LogscaleMenuButton';
import { SessionSummary } from '../SessionSummary';
import { PayloadTabs } from './PayloadTabs';
import { useDetectionContext } from '@investigator/features/detections/context';
import { AppConfigurationsContext } from '@investigator/shared/providers';

const { alerts, detection } = i18n.pages.detections;

interface AlertRowSuricataProps {
  item: SanitizedSuricataAlert;
  logScaleQuery?: string;
  showContent?: boolean;
}

interface AlertRowSplitSuricataProps {
  item: SanitizedSuricataAlert;
  loading: boolean | undefined;
  suricataRuleText: string;
  isGPTPrivateDataEnabled: boolean;
  logScaleQuery?: string;
}

// Local type for SuricataAlert with severity and is_custom_severity
interface SuricataAlertWithSeverity extends SanitizedSuricataAlert {
  severity?: number;
  is_custom_severity?: boolean;
}

export const SuricataButtonsCol = ({
  item,
  showContent,
  logScaleQuery,
}: AlertRowSuricataProps) => {
  const {
    isOpen: investigateModalIsOpen,
    onOpen: investigateModalOnOpen,
    onClose: investigateModalOnClose,
  } = useDisclosure();
  const formattedTime = moment
    .unix(item.alert_timestamp?.observed ?? 0)
    .format();
  const start = moment(formattedTime).subtract(1, 'hours').unix();
  const end = moment(formattedTime).add(24, 'hours').unix();

  const searchQueryString = buildSearchQueryString({
    item: {
      ...item,
      alert_name: item?.alert_info?.alert_name ?? '',
      alert_info: {
        alert_type: item.alert_info?.alert_type ?? '',
        alert_name: item.alert_info?.alert_name ?? '',
      },
      alert_timestamp: { observed: item.alert_timestamp?.observed ?? 0 },
      alert_entity: {
        entity_name: item.alert_entity?.entity_name ?? '',
        entity_type: item.alert_entity?.entity_type ?? '',
      },
      severity: (item as SuricataAlertWithSeverity).severity ?? 0,
      is_custom_severity:
        (item as SuricataAlertWithSeverity).is_custom_severity ?? false,
      first_seen: 0,
      last_seen: 0,
    },
    logScaleQuery,
    start,
    end,
  });

  return (
    <Flex flexGrow={1}>
      <LogscaleMenuButton
        searchQueryString={searchQueryString}
        investigateModalOnOpen={investigateModalOnOpen}
        showContent={showContent}
      />
      <InvestigateLogsTableModal
        isOpen={investigateModalIsOpen}
        onClose={investigateModalOnClose}
        onOpen={investigateModalOnOpen}
        alertsName={item?.alert_info?.alert_name ?? ''}
        alertsUid={item?.uid}
        alertsTimestamp={formattedTime}
        alertsTenant={item?.tenant}
        logScaleQuery={logScaleQuery}
      />
    </Flex>
  );
};

export const AlertRowSuricata = ({
  item,
  logScaleQuery,
}: AlertRowSuricataProps) => {
  return (
    <HStack spacing={4}>
      <VStack alignItems="start">
        <TimestampCell
          timestamp={item?.alert_timestamp?.observed ?? 0}
          isFromNow={false}
        />
        <HStack color="text.200">
          <Text textStyle="body-md">{item?.alert_info?.alert_name ?? ''}</Text>
          <Text textStyle="body-md">|</Text>
          <Text textStyle="body-md">
            {item?.alert_entity?.entity_name ?? ''}
          </Text>
        </HStack>
      </VStack>
      <SuricataButtonsCol item={item} logScaleQuery={logScaleQuery} />
    </HStack>
  );
};

export const AlertRowSplitSuricata = ({
  item,
  loading,
  suricataRuleText,
  isGPTPrivateDataEnabled,
  logScaleQuery,
}: AlertRowSplitSuricataProps) => {
  const { appConfigurations } = useContext(AppConfigurationsContext);
  const { token: sessionToken } = useHumioToken();
  const { detectionData } = useDetectionContext();
  const isLLMSummary = appConfigurations?.feature_flags.llm_summary || false;
  const [seeMore, setSeeMore] = useState<boolean>(false);
  const [payload, setPayload] = useState<string>('');
  const [payloadSummary, setPayloadSummary] = useState<string>('');
  const [sessionSummaryData, setSessionSummaryData] = useState<
    Maybe<AlertSessionSummary> | undefined
  >();
  const {
    isOpen: payloadIsOpen,
    onOpen: payloadOnOpen,
    onClose: payloadOnClose,
  } = useDisclosure();

  const time = moment.unix(item?.alert_timestamp?.observed ?? 0);
  const formattedTime = time.format();

  const alertQueryVariables = useMemo(
    () => ({
      query: buildAlertIdQuery(item.alert_id),
      log_search_client_session_id: sessionToken || '',
    }),
    [item.alert_id, sessionToken],
  );

  const [fetchPayload, { loading: payloadLoading, error: payloadError }] =
    useLazyQuery<
      QueryAlertsSuricataPayloadPaginatedQuery,
      QueryAlertsSuricataPayloadPaginatedQueryVariables
    >(QUERY_ALERTS_SURICATA_PAYLOAD, {
      onCompleted: (data) => {
        const alert = data?.queryAlertsPaginated?.alerts?.[0];
        // This check is here to satisfy typescript without casting.
        // The union type considers all alerts because we use a
        // paginated query
        if (alert?.__typename === 'SuricataAlert') {
          setPayload(alert?.payload ?? '');
          setPayloadSummary(alert?.payload_summary ?? '');
          setSessionSummaryData(alert?.session_summary);
        } else {
          console.error(`Wanted Suricata Alert but got: ${alert?.__typename}`);
        }
      },
      onError: (error) => {
        console.error('error: ', error);
      },
    });

  const [
    fetchPayloadLLMDisabled,
    { loading: payloadLLMDisabledLoading, error: payloadLLMDisabledError },
  ] = useLazyQuery<
    QueryAlertsSuricataPayloadLLmDisabledPaginatedQuery,
    QueryAlertsSuricataPayloadLLmDisabledPaginatedQueryVariables
  >(QUERY_ALERTS_SURICATA_PAYLOAD_LLM_DISABLED, {
    onCompleted: (data) => {
      const alert = data?.queryAlertsPaginated?.alerts?.[0];
      // This check is here to satisfy typescript without casting.
      // The union type considers all alerts because we use a
      // paginated query
      if (alert?.__typename === 'SuricataAlert') {
        setPayload(alert?.payload ?? '');
        setPayloadSummary('');
        setSessionSummaryData(undefined);
      } else {
        console.error(`Wanted Suricata Alert but got: ${alert?.__typename}`);
      }
    },
    onError: (error) => {
      console.error('error: ', error);
    },
  });

  useEffect(() => {
    if (
      detectionData?.tenant_info?.tenant_id &&
      detectionData?.alert_entity?.entity_id
    ) {
      isLLMSummary && isGPTPrivateDataEnabled
        ? fetchPayload({ variables: alertQueryVariables })
        : fetchPayloadLLMDisabled({ variables: alertQueryVariables });
    }
  }, [
    detectionData,
    fetchPayload,
    fetchPayloadLLMDisabled,
    isGPTPrivateDataEnabled,
    isLLMSummary,
    alertQueryVariables,
  ]);

  if (loading) {
    return (
      <Center w="100%">
        <GraphQLReqStatus loading={loading} />
      </Center>
    );
  }

  return (
    <VStack mb={2} w="100%" alignItems="start" gap={0}>
      <Box w="100%">
        <LogscaleDetails
          alertsUid={item?.uid}
          alertsName={item.alert_info?.alert_name ?? ''}
          alertsTimestamp={formattedTime}
          alertsTenant={item?.tenant}
          logScaleQuery={logScaleQuery}
          alertType="suricata_corelight"
          childrenBelowHeader={
            <>
              <AccordionPanel
                w="100%"
                title={detection.alertDetails}
                data-testid="suricata-alerts-wrapper"
                reduceMotion
              >
                <DetailsGrid>
                  <DetailsGridRow title={alerts.name}>
                    <Text overflowWrap="anywhere">
                      {item?.alert_info?.alert_name}
                    </Text>
                  </DetailsGridRow>
                  <DetailsGridRow title={alerts.timestamp}>
                    <Text>{formattedTime}</Text>
                  </DetailsGridRow>
                  <DetailsGridRow title={alerts.uid}>
                    <Text>{item?.uid}</Text>
                  </DetailsGridRow>
                  <DetailsGridRow title={alerts.sourceIp}>
                    <Text>{item?.alert_entity?.entity_name}</Text>
                  </DetailsGridRow>
                  <DetailsGridRow title={alerts.destinationIp}>
                    <Text>{item?.destination_ip}</Text>
                  </DetailsGridRow>
                  <DetailsGridRow title={alerts.type}>
                    <Text>{item?.alert_info?.alert_type}</Text>
                  </DetailsGridRow>
                </DetailsGrid>
                {isLLMSummary && isGPTPrivateDataEnabled && (
                  <SessionSummary
                    loading={payloadLoading || payloadLLMDisabledLoading}
                    // TODO: Update this with correct types
                    sessionSummaryData={
                      sessionSummaryData as SessionSummaryData
                    }
                  />
                )}
              </AccordionPanel>
              {isLLMSummary && isGPTPrivateDataEnabled ? (
                <AccordionPanel
                  w="100%"
                  title={
                    <HStack>
                      <ChatGPTIcon boxSize={5} />{' '}
                      <Text>{detection.payloadSummary}</Text>
                    </HStack>
                  }
                  reduceMotion
                >
                  {payloadLoading ||
                  payloadLLMDisabledLoading ||
                  payloadError ||
                  payloadLLMDisabledError ? (
                    <Center w="100%">
                      <GraphQLReqStatus
                        loading={payloadLoading || payloadLLMDisabledLoading}
                      />
                      {(payloadError || payloadLLMDisabledError) && (
                        <Text> {detection.payloadError}</Text>
                      )}
                    </Center>
                  ) : payloadSummary ? (
                    <>
                      <Text noOfLines={seeMore ? undefined : 4}>
                        {payloadSummary}
                      </Text>
                      <VStack alignItems="start">
                        <Button
                          variant="link"
                          onClick={() => setSeeMore(!seeMore)}
                        >
                          {seeMore
                            ? alerts.suricata.seeLess
                            : alerts.suricata.seeMore}
                        </Button>
                        <Button
                          variant="solid"
                          colorScheme="gray"
                          mt={2}
                          onClick={payloadOnOpen}
                        >
                          {alerts.suricata.payload.viewPayload}
                        </Button>
                      </VStack>
                    </>
                  ) : (
                    <Text>{detection.payloadUnavailableMessage}</Text>
                  )}
                </AccordionPanel>
              ) : (
                <AccordionPanel w="100%" title={detection.payload} reduceMotion>
                  {payloadLoading ||
                  payloadLLMDisabledLoading ||
                  payloadError ||
                  payloadLLMDisabledError ? (
                    <Center w="100%">
                      <GraphQLReqStatus
                        loading={payloadLoading || payloadLLMDisabledLoading}
                      />
                      {(payloadError || payloadLLMDisabledError) && (
                        <Text> {detection.payloadError}</Text>
                      )}
                    </Center>
                  ) : payload ? (
                    <>
                      <PayloadTabs payload={payload} />
                      <Button
                        variant="solid"
                        colorScheme="gray"
                        mt={2}
                        onClick={payloadOnOpen}
                      >
                        {alerts.suricata.payload.viewPayload}
                      </Button>
                    </>
                  ) : (
                    <Text>{detection.payloadUnavailableMessage}</Text>
                  )}
                </AccordionPanel>
              )}
            </>
          }
        />
      </Box>
      <Modal
        size="xl"
        isOpen={payloadIsOpen}
        onClose={payloadOnClose}
        title={detection.payload}
        body={
          <Board>
            {suricataRuleText && (
              <Column>
                <Text fontSize="large" py={3}>
                  {detection.rule}
                </Text>
                <SnippetBox
                  snippet={dedent`${suricataRuleText}`}
                  isCopyable={true}
                  height="160px"
                  overflow="scroll"
                />
              </Column>
            )}
            {payload && (
              <Column>
                <Text fontSize="large" py={3}>
                  {detection.payload}
                </Text>
                <PayloadTabs isModal={true} payload={payload} />
              </Column>
            )}
          </Board>
        }
        footer={
          <Button variant="solid" colorScheme="gray" onClick={payloadOnClose}>
            {detection.close}
          </Button>
        }
      />
    </VStack>
  );
};
